import { forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { classNames } from 'primereact/utils';
import { CheckIconV2, CopyIcon, useGlobalToast } from '@repo/atomic-components';
import type { MedicalMatchEntry, MedicalMatchPage } from '@/api';
import { OutlineIconButton } from '@/ui/components/OutlineIconButton';
import { HighlightedText } from './HighlightedText';
import {
  MEDICAL_MATCH_COPY_FAILED_DETAIL,
  MEDICAL_MATCH_COPY_FAILED_SUMMARY,
  MEDICAL_MATCH_CREATION_TYPE_BADGE_CLASS,
  MEDICAL_MATCH_CREATION_TYPE_LABEL,
} from './constants';
import { matchesSearchTerm, writeToClipboard } from './utils';

interface UniquePageWithSnippets {
  page: MedicalMatchPage;
  snippets: string[];
}

const groupPagesByIdWithSnippets = (pages: MedicalMatchPage[]): UniquePageWithSnippets[] => {
  const map = new Map<string, UniquePageWithSnippets>();
  for (const p of pages) {
    const id = p.pageInfo.pageId;
    const existing = map.get(id);
    if (existing) {
      if (p.snippet) {
        existing.snippets.push(p.snippet);
      }
    } else {
      map.set(id, { page: p, snippets: p.snippet ? [p.snippet] : [] });
    }
  }
  return Array.from(map.values());
};

type MedicalMatchEntryBlockProps = {
  entry: MedicalMatchEntry;
  onPageNumberClick: (pageId: string, snippets: string[]) => void;
  getPageNumber: (pageId: string) => number;
  visitedPageIds: ReadonlySet<string>;
  /** Set for a few seconds after the panel scrolls to this card as a newly landed live answer. */
  isFlashing: boolean;
  /** Normalised panel search term; empty when nothing is being filtered. */
  searchQuery: string;
};

export function MedicalMatchEntryBlock({
  entry,
  onPageNumberClick,
  getPageNumber,
  visitedPageIds,
  isFlashing,
  searchQuery,
}: MedicalMatchEntryBlockProps) {
  const groupedPages = useMemo(() => groupPagesByIdWithSnippets(entry.pages), [entry.pages]);
  const indexedPages = useMemo(
    () =>
      groupedPages.map(({ page, snippets }, index) => ({
        page,
        snippets,
        displayIndex: index + 1,
        isDisabled: getPageNumber(page.pageInfo.pageId) === -1,
      })),
    [groupedPages, getPageNumber],
  );

  const firstEnabledReference = indexedPages.find((p) => !p.isDisabled);
  const firstEnabledChipRef = useRef<HTMLButtonElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  const handleBoxClick = () => {
    if (firstEnabledReference) {
      onPageNumberClick(firstEnabledReference.page.pageInfo.pageId, firstEnabledReference.snippets);
      const chipEl = firstEnabledChipRef.current as
        | (HTMLButtonElement & { _suppressNextFocusTooltip?: () => void })
        | null;
      chipEl?._suppressNextFocusTooltip?.();
      chipEl?.focus();
    }
  };

  // Copy-success flag drives the icon swap from the copy glyph to a green
  // tick for 1.6s after a successful clipboard write. Timer is ref-tracked so
  // repeated clicks reset the window cleanly instead of flickering mid-anim.
  // A refused write gets a toast instead.
  const globalToastRef = useGlobalToast();
  const [isCopied, setIsCopied] = useState(false);
  const copiedResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (copiedResetTimerRef.current) clearTimeout(copiedResetTimerRef.current);
    },
    [],
  );
  const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    writeToClipboard(`${entry.question}\n\n${entry.answer}`)
      .then(() => {
        setIsCopied(true);
        if (copiedResetTimerRef.current) clearTimeout(copiedResetTimerRef.current);
        copiedResetTimerRef.current = setTimeout(() => setIsCopied(false), 1600);
      })
      .catch((error) => {
        console.error('Failed to copy Medical Match answer:', error);
        globalToastRef.current?.show({
          severity: 'error',
          summary: MEDICAL_MATCH_COPY_FAILED_SUMMARY,
          detail: MEDICAL_MATCH_COPY_FAILED_DETAIL,
          closable: true,
          life: 5000,
        });
      });
  };

  return (
    <div
      role={firstEnabledReference ? 'button' : undefined}
      tabIndex={firstEnabledReference ? 0 : undefined}
      onClick={firstEnabledReference ? handleBoxClick : undefined}
      onKeyDown={
        firstEnabledReference
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleBoxClick();
              }
            }
          : undefined
      }
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      // How useLiveAnswerScroll finds this card on screen.
      data-entry-id={entry.entryId}
      className={classNames(
        'relative flex flex-col gap-[24px] rounded-lg border border-gray-200 bg-white p-[24px] transition-shadow',
        firstEnabledReference && 'cursor-pointer hover:shadow-[0_2px_8px_rgba(0,0,0,0.1)]',
        isFlashing && 'animate-medical-match-entry-flash',
      )}
    >
      {/* Copy button is visible on hover, OR while a copy-success tick is
          still animating — otherwise the mouse leaving the block mid-tick
          would abruptly unmount the icon before the confirmation finishes. */}
      {(isHovered || isCopied) && (
        <div className="absolute top-[12px] right-[12px]">
          <OutlineIconButton
            icon={
              isCopied ? (
                <CheckIconV2
                  key="copied"
                  className="text-fletchers-green-600"
                  style={{ animation: 'copy-success-pop 300ms cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}
                />
              ) : (
                <CopyIcon key="copy" />
              )
            }
            onClick={handleCopy}
            title={isCopied ? 'Copied' : 'Copy to clipboard'}
            aria-label={isCopied ? 'Copied' : 'Copy'}
          />
        </div>
      )}
      {/* Where the question came from — Standard / Custom / Live. Sits above
          the question, left of the hover copy button. */}
      <span
        className={classNames(
          'w-fit rounded-md px-[10px] py-[4px] text-[13px] font-medium',
          MEDICAL_MATCH_CREATION_TYPE_BADGE_CLASS[entry.creationType],
        )}
      >
        {MEDICAL_MATCH_CREATION_TYPE_LABEL[entry.creationType]}
      </span>
      <p className="pr-[44px] text-[14px] font-semibold text-gray-900">
        <HighlightedText text={entry.question} searchQuery={searchQuery} />
      </p>
      <ExpandableAnswer answer={entry.answer} hasReferences={indexedPages.length > 0} searchQuery={searchQuery} />
      {indexedPages.length > 0 && (
        <div className="flex flex-wrap gap-2.5">
          {indexedPages.map(({ page, snippets, displayIndex, isDisabled }) => (
            <ReferenceChip
              key={page.pageInfo.pageId}
              ref={
                page.pageInfo.pageId === firstEnabledReference?.page.pageInfo.pageId ? firstEnabledChipRef : undefined
              }
              displayIndex={displayIndex}
              isDisabled={isDisabled}
              isVisited={visitedPageIds.has(page.pageInfo.pageId)}
              snippets={snippets}
              onClick={(e) => {
                e.stopPropagation();
                onPageNumberClick(page.pageInfo.pageId, snippets);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ExpandableAnswer({
  answer,
  hasReferences,
  searchQuery,
}: {
  answer: string;
  hasReferences: boolean;
  searchQuery: string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  const isExpandedRef = useRef(isExpanded);
  isExpandedRef.current = isExpanded;
  // One-shot guard for the search auto-expand below: without it, a user who
  // hits "Read less" while a term is still in the search box would have the
  // answer reopened under them on the next render.
  const hasAutoExpandedRef = useRef(false);

  const matchesSearch = matchesSearchTerm(answer, searchQuery);

  // A new answer, or the answer moving in or out of the current search, changes
  // what ought to be on show — so drop back to clamped and let the measuring
  // effect decide again. Tracking whether it *matches* rather than the term
  // itself keeps this off the per-keystroke path: the flag flips at most twice
  // while the user types, so we don't re-measure every card on every letter.
  const prevAnswerRef = useRef(answer);
  const prevMatchesSearchRef = useRef(matchesSearch);
  if (prevAnswerRef.current !== answer || prevMatchesSearchRef.current !== matchesSearch) {
    prevAnswerRef.current = answer;
    prevMatchesSearchRef.current = matchesSearch;
    setIsExpanded(false);
    setIsClamped(false);
    hasAutoExpandedRef.current = false;
  }

  useLayoutEffect(() => {
    if (isExpanded) return;
    const el = textRef.current;
    if (!el) return;
    setIsClamped(el.scrollHeight > el.clientHeight);

    // The five-line clamp would otherwise hide the very words the user
    // searched for, leaving a card that reads as a false positive. Expanding
    // here rather than in the initial state buys the measurement above one
    // clamped pass, which is what leaves a "Read less" control to put the
    // answer back — and being a layout effect, that pass is never painted.
    if (matchesSearch && !hasAutoExpandedRef.current) {
      hasAutoExpandedRef.current = true;
      setIsExpanded(true);
    }

    const observer = new ResizeObserver(() => {
      if (isExpandedRef.current) return;
      setIsClamped(el.scrollHeight > el.clientHeight);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [answer, isExpanded, matchesSearch]);

  return (
    <div className="flex flex-col gap-1">
      {/* An answer with no citations behind it is a "nothing found in the
          records" result, so it's played down (grey + italic) to read as an
          absence of evidence rather than a finding. */}
      <p
        ref={textRef}
        className={classNames(
          'font-sans text-[14px] leading-[normal] font-normal whitespace-pre-wrap',
          hasReferences ? 'text-fletchers-black not-italic' : 'text-fletchers-gray-200 italic',
          !isExpanded && 'line-clamp-5',
        )}
      >
        <HighlightedText text={answer} searchQuery={searchQuery} />
      </p>
      {isClamped && (
        <button
          type="button"
          aria-expanded={isExpanded}
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded((prev) => !prev);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
            }
          }}
          className="text-fletchers-green-600 cursor-pointer self-end text-[13px] font-medium hover:underline"
        >
          {isExpanded ? 'Read less' : '...Read more'}
        </button>
      )}
    </div>
  );
}

type ReferenceChipProps = {
  displayIndex: number;
  isDisabled: boolean;
  isVisited: boolean;
  snippets: string[];
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

const ReferenceChip = forwardRef<HTMLButtonElement, ReferenceChipProps>(function ReferenceChip(
  { displayIndex, isDisabled, isVisited, snippets, onClick },
  forwardedRef,
) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const suppressNextFocusTooltipRef = useRef(false);
  const [chipRect, setChipRect] = useState<DOMRect | null>(null);
  // Vertical placement is decided after the tooltip mounts — we measure its
  // rendered height and flip above the chip when the below position would
  // overflow the viewport. `null` while the first paint is still hidden.
  const [placement, setPlacement] = useState<'below' | 'above' | null>(null);

  const setRefs = (el: HTMLButtonElement | null) => {
    buttonRef.current = el;
    if (el) {
      (el as HTMLButtonElement & { _suppressNextFocusTooltip?: () => void })._suppressNextFocusTooltip = () => {
        suppressNextFocusTooltipRef.current = true;
      };
    }
    if (typeof forwardedRef === 'function') forwardedRef(el);
    else if (forwardedRef) forwardedRef.current = el;
  };

  const showTooltip = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      setChipRect(rect);
      setPlacement(null);
    }
  };
  const hideTooltip = () => {
    setChipRect(null);
    setPlacement(null);
  };

  // After the tooltip renders, measure it and flip above the chip if the
  // "below" position would overflow the bottom of the viewport. Runs once
  // per show — placement stays fixed while the tooltip is open so it
  // doesn't jitter if the viewport size wobbles mid-hover.
  useLayoutEffect(() => {
    if (!chipRect || placement !== null) return;
    const tooltipEl = tooltipRef.current;
    if (!tooltipEl) return;
    const tooltipHeight = tooltipEl.getBoundingClientRect().height;
    const gap = 8;
    const viewportH = window.innerHeight;
    const wouldOverflowBelow = chipRect.bottom + gap + tooltipHeight > viewportH;
    // Only flip up if there's actually more room above; otherwise stick with
    // below (the browser will just clip a hair rather than the alternative
    // clipping harder).
    const roomAbove = chipRect.top;
    setPlacement(wouldOverflowBelow && roomAbove >= tooltipHeight + gap ? 'above' : 'below');
  }, [chipRect, placement]);

  const handleFocus = () => {
    if (suppressNextFocusTooltipRef.current) {
      suppressNextFocusTooltipRef.current = false;
      return;
    }
    showTooltip();
  };

  const tooltipBody = isDisabled
    ? 'Reference removed by filters'
    : snippets.length > 0
      ? snippets.map((s) => `"${s}"`).join('\n\n')
      : 'No snippet available';

  return (
    <>
      <button
        ref={setRefs}
        type="button"
        disabled={isDisabled}
        onClick={onClick}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={handleFocus}
        onBlur={hideTooltip}
        className={`enabled:hover:bg-fletchers-green-100 enabled:hover:text-fletchers-green-600 enabled:focus:bg-fletchers-green-100 enabled:focus:text-fletchers-green-600 flex min-w-[20px] flex-col items-center justify-center gap-2.5 rounded-[4px] px-[6px] py-[2px] text-xs font-medium focus:outline-none enabled:cursor-pointer enabled:hover:opacity-100 enabled:focus:opacity-100 disabled:opacity-50 ${
          isVisited && !isDisabled
            ? 'bg-fletchers-gray-75 text-gray-400 opacity-60'
            : 'bg-fletchers-gray-90 text-gray-700'
        }`}
      >
        {displayIndex}
      </button>
      {chipRect &&
        createPortal(
          <div
            ref={tooltipRef}
            style={{
              position: 'fixed',
              // Below by default; flipped above once measurement completes.
              // While `placement` is null we render off-DOM-visible via
              // `visibility: hidden` so the below-then-jump isn't seen.
              top:
                placement === 'above'
                  ? chipRect.top - 8 - (tooltipRef.current?.getBoundingClientRect().height ?? 0)
                  : chipRect.bottom + 8,
              left: chipRect.left,
              visibility: placement === null ? 'hidden' : 'visible',
              width: 280,
              background: 'white',
              border: '1px solid #E1E1E0',
              borderRadius: 6,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              padding: '12px 16px',
              fontSize: 12,
              lineHeight: 1.5,
              color: '#6E6E6E',
              fontStyle: 'italic',
              whiteSpace: 'pre-wrap',
              zIndex: 1000,
            }}
          >
            {tooltipBody}
          </div>,
          document.body,
        )}
    </>
  );
});
